import React from 'react';
import { useTranslation } from '@/hooks/useTranslation';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { getImageAssetPath } from '@/lib/imageHelper';

const ProofSection = () => {
  const { t } = useTranslation();

  // Profilbilder laden
  const ddiImagePath = getImageAssetPath('ddi.jpeg');
  const sbrImagePath = getImageAssetPath('sbr.jpeg');

  const stats = [
    {
      value: t('salescockpitPage.proof.stats.timeSaving.value'),
      description: t('salescockpitPage.proof.stats.timeSaving.description')
    },
    {
      value: t('salescockpitPage.proof.stats.errorReduction.value'),
      description: t('salescockpitPage.proof.stats.errorReduction.description')
    },
    {
      value: t('salescockpitPage.proof.stats.fasterProcessing.value'),
      description: t('salescockpitPage.proof.stats.fasterProcessing.description')
    },
    {
      value: t('salescockpitPage.proof.stats.increasedVolume.value'),
      description: t('salescockpitPage.proof.stats.increasedVolume.description')
    }
  ];

  const testimonials = [
    {
      quote: t('salescockpitPage.proof.testimonials.dennis.quote'),
      author: t('salescockpitPage.proof.testimonials.dennis.author'),
      position: t('salescockpitPage.proof.testimonials.dennis.position'),
      imagePath: ddiImagePath,
      fallback: 'DD'
    },
    {
      quote: t('salescockpitPage.proof.testimonials.simon.quote'),
      author: t('salescockpitPage.proof.testimonials.simon.author'),
      position: t('salescockpitPage.proof.testimonials.simon.position'),
      imagePath: sbrImagePath,
      fallback: 'SB'
    }
  ];

  return (
    <section className="py-20 bg-gradient-to-b from-white to-blue-50">
      <div className="container mx-auto px-4">
        <div className="max-w-3xl mx-auto text-center mb-16">
          <h2 className="text-3xl md:text-4xl font-bold mb-6 text-blue-900">
            {t('salescockpitPage.proof.title')}
          </h2>
          <p className="text-xl text-gray-700">
            {t('salescockpitPage.proof.subtitle')}
          </p>
        </div>

        {/* Statistiken */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-8 mb-16">
          {stats.map((stat, index) => (
            <div key={index} className="text-center p-6 bg-white rounded-xl shadow-md hover:shadow-lg transition-shadow">
              <div className="text-4xl md:text-5xl font-bold text-blue-600 mb-3">
                {stat.value}
              </div>
              <p className="text-gray-700">
                {stat.description}
              </p>
            </div>
          ))}
        </div>

        {/* Entwickler-Testimonials */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-6xl mx-auto">
          {testimonials.map((testimonial, index) => (
            <div 
              key={index}
              className="bg-white p-8 rounded-xl shadow-md hover:shadow-xl transition-all duration-300"
            >
              <div className="mb-6 text-blue-500">
                <svg width="48" height="36" viewBox="0 0 48 36" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M13.248 0C8.496 0 4.68 1.44 1.8 4.32C0.6 5.52 0 7.08 0 9C0 10.92 0.6 12.48 1.8 13.68C3 14.88 4.56 15.48 6.48 15.48C8.4 15.48 9.96 14.88 11.16 13.68C12.36 12.48 12.96 10.92 12.96 9C12.96 7.08 12.36 5.52 11.16 4.32C9.96 3.12 8.4 2.52 6.48 2.52C6.24 2.52 6.048 2.532 5.904 2.556C5.76 2.58 5.592 2.628 5.4 2.7C7.32 2.7 8.94 3.36 10.26 4.68C11.58 6 12.24 7.68 12.24 9.72C12.24 12.72 11.076 15.3 8.748 17.46C6.42 19.62 3.648 20.7 0.432 20.7L0 23.4C4.56 23.4 8.4 22.08 11.52 19.44C14.64 16.8 16.2 13.68 16.2 10.08C16.2 7.44 15.36 5.04 13.68 2.88C12 0.72 9.696 0 6.768 0H13.248ZM36.048 0C31.296 0 27.48 1.44 24.6 4.32C23.4 5.52 22.8 7.08 22.8 9C22.8 10.92 23.4 12.48 24.6 13.68C25.8 14.88 27.36 15.48 29.28 15.48C31.2 15.48 32.76 14.88 33.96 13.68C35.16 12.48 35.76 10.92 35.76 9C35.76 7.08 35.16 5.52 33.96 4.32C32.76 3.12 31.2 2.52 29.28 2.52C29.04 2.52 28.848 2.532 28.704 2.556C28.56 2.58 28.392 2.628 28.2 2.7C30.12 2.7 31.74 3.36 33.06 4.68C34.38 6 35.04 7.68 35.04 9.72C35.04 12.72 33.876 15.3 31.548 17.46C29.22 19.62 26.448 20.7 23.232 20.7L22.8 23.4C27.36 23.4 31.2 22.08 34.32 19.44C37.44 16.8 39 13.68 39 10.08C39 7.44 38.16 5.04 36.48 2.88C34.8 0.72 32.496 0 29.568 0H36.048Z" fill="currentColor"/>
                </svg>
              </div>
              <blockquote className="text-gray-700 mb-6 italic">
                "{testimonial.quote}"
              </blockquote>
              <div className="flex items-center">
                <Avatar className="w-12 h-12 border-2 border-blue-200">
                  <AvatarImage src={testimonial.imagePath} alt={testimonial.author} />
                  <AvatarFallback className="bg-blue-100 text-blue-700 font-bold text-xl">
                    {testimonial.fallback}
                  </AvatarFallback>
                </Avatar>
                <div className="ml-4">
                  <p className="font-semibold text-gray-900">{testimonial.author}</p>
                  <p className="text-sm text-gray-600">{testimonial.position}</p>
                </div>
              </div>
            </div>
          ))}
        </div>

        <div className="max-w-3xl mx-auto text-center mt-16">
          <p className="text-xl font-semibold text-blue-700">
            {t('salescockpitPage.proof.conclusion')}
          </p>
        </div>
      </div>
    </section>
  );
};

export default ProofSection; 